/**
 * Jeu d'icones BUL.
 *
 * Grille de 24, trait de 1,75, extrémités et jonctions arrondies. Les tracés sont
 * identiques a ceux de la référence HTML (`bul/design/assets/bul-icons.js`), pour
 * que le web et le mobile ne divergent jamais.
 *
 * Les variantes `…Fill` marquent l'onglet actif dans la barre basse, et le geste
 * déja accompli sur une annonce : la réaction posée, le signet de
 * l'enregistrement. Plein contre trait, c'est le seul contraste qui se voit sans
 * couleur.
 */

export type IconPath = {
  d: string;
  /** Tracé plein plutot qu'au trait. */
  fill?: boolean;
  fillRule?: 'evenodd';
  /** Couleur figée, indépendante de la couleur de l'icone (coche blanche du label). */
  color?: string;
  /** Épaisseur de trait spécifique. */
  strokeWidth?: number;
};

const p = (d: string): IconPath => ({ d });
const f = (d: string, fillRule?: 'evenodd'): IconPath => ({ d, fill: true, fillRule });
/** Tracé au trait avec réglages spécifiques (épaisseur, couleur figée). */
const s = (d: string, options: Omit<IconPath, 'd'>): IconPath => ({ d, ...options });

/** Cercle exprimé en tracé : `react-native-svg` accepte `<Circle>`, mais un
 * registre uniquement composé de `d` reste beaucoup plus simple a parcourir. */
const circle = (cx: number, cy: number, r: number): string =>
  `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${r * 2} 0a${r} ${r} 0 1 0 ${-r * 2} 0z`;

const dot = (cx: number, cy: number, r: number): IconPath => f(circle(cx, cy, r));

export const iconPaths = {
  // ---------------------------------------------------------------- navigation
  home: [
    p('M3.5 10.6 12 3.6l8.5 7v8.9a1.5 1.5 0 0 1-1.5 1.5h-4.2v-6h-5.6v6H5a1.5 1.5 0 0 1-1.5-1.5Z'),
  ],
  homeFill: [
    f(
      'M3.5 10.6 12 3.6l8.5 7v8.9a1.5 1.5 0 0 1-1.5 1.5H5a1.5 1.5 0 0 1-1.5-1.5Zm5.7 10.4v-6h5.6v6Z',
      'evenodd',
    ),
  ],
  compass: [p(circle(12, 12, 8.6)), p('m15.6 8.4-2.1 5.1-5.1 2.1 2.1-5.1Z')],
  compassFill: [
    f(
      'M12 3.4a8.6 8.6 0 1 0 0 17.2 8.6 8.6 0 0 0 0-17.2Zm3.6 5-2.1 5.1-5.1 2.1 2.1-5.1Z',
      'evenodd',
    ),
  ],
  search: [p(circle(11, 11, 7)), p('m20 20-3.9-3.9')],
  plus: [p('M12 5v14M5 12h14')],
  minus: [p('M5 12h14')],
  plusSquare: [
    p(
      'M8 3.4h8a4.6 4.6 0 0 1 4.6 4.6v8a4.6 4.6 0 0 1-4.6 4.6H8a4.6 4.6 0 0 1-4.6-4.6V8A4.6 4.6 0 0 1 8 3.4Z',
    ),
    p('M12 8.4v7.2M8.4 12h7.2'),
  ],
  plusSquareFill: [
    f(
      'M7.6 3.4h8.8a4.2 4.2 0 0 1 4.2 4.2v8.8a4.2 4.2 0 0 1-4.2 4.2H7.6a4.2 4.2 0 0 1-4.2-4.2V7.6a4.2 4.2 0 0 1 4.2-4.2Zm3.5 5v2.7H8.4v1.8h2.7v2.7h1.8v-2.7h2.7v-1.8h-2.7V8.4Z',
      'evenodd',
    ),
  ],
  chat: [
    p(
      'M20.5 11.5c0 4.4-3.8 8-8.5 8a9.6 9.6 0 0 1-3.7-.7L3.5 20.5l1.5-4.4a7.7 7.7 0 0 1-1.5-4.6c0-4.4 3.8-8 8.5-8s8.5 3.6 8.5 8Z',
    ),
  ],
  chatFill: [
    f(
      'M12 3.5c-4.7 0-8.5 3.6-8.5 8 0 1.7.6 3.3 1.5 4.6L3.5 20.5l4.3-1.2c1.2.5 2.4.7 3.7.7 4.7 0 8.5-3.6 8.5-8s-3.8-8-8.5-8Z',
    ),
  ],
  bag: [
    p('M5.8 8h12.4l1 11.6a1.4 1.4 0 0 1-1.4 1.5H6.2a1.4 1.4 0 0 1-1.4-1.5Z'),
    p('M9 8.6V6.4a3 3 0 0 1 6 0v2.2'),
  ],
  bagFill: [
    f('M5.8 8.6h12.4l1 11a1.4 1.4 0 0 1-1.4 1.5H6.2a1.4 1.4 0 0 1-1.4-1.5Z'),
    s('M9 8.6V6.4a3 3 0 0 1 6 0v2.2', { strokeWidth: 1.9 }),
  ],
  user: [p(circle(12, 8.2, 4)), p('M4.6 20.4a7.6 7.6 0 0 1 14.8 0')],
  userFill: [f(circle(12, 8.2, 4)), f('M4.6 20.4a7.6 7.6 0 0 1 14.8 0Z')],
  users: [
    p(circle(9.2, 8.4, 3.6)),
    p('M2.8 20.2a6.6 6.6 0 0 1 12.8 0'),
    p('M16.2 5.4a3.6 3.6 0 0 1 0 6.1'),
    p('M17.6 14.6a6.6 6.6 0 0 1 3.9 5.6'),
  ],
  bell: [
    p('M18 9.6a6 6 0 1 0-12 0c0 5.2-2 6.4-2 6.4h16s-2-1.2-2-6.4Z'),
    p('M13.7 19.5a2 2 0 0 1-3.4 0'),
  ],

  // Les deux façons de regarder une collection, toujours par paire : la mosaique
  // et la liste. Les carrés de `grid` sont ceux de la référence HTML (`i-grid`),
  // écrits en tracé — le registre ne connait que des `d`.
  grid: [
    p('M5.5 3.5h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-3a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2Z'),
    p('M15.5 3.5h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-3a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2Z'),
    p('M5.5 13.5h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-3a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2Z'),
    p('M15.5 13.5h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-3a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2Z'),
  ],
  list: [
    dot(5, 6.6, 1.3),
    dot(5, 12, 1.3),
    dot(5, 17.4, 1.3),
    p('M9.8 6.6h10.7'),
    p('M9.8 12h10.7'),
    p('M9.8 17.4h10.7'),
  ],

  // -------------------------------------------------------------------- fleches
  chevronRight: [p('m9.5 5 7 7-7 7')],
  chevronLeft: [p('m14.5 5-7 7 7 7')],
  chevronDown: [p('m6 9.5 6 6 6-6')],
  chevronUp: [p('m6 14.5 6-6 6 6')],
  arrowLeft: [p('M19.5 12H4.5'), p('m10.5 6-6 6 6 6')],
  arrowRight: [p('M4.5 12h15'), p('m13.5 6 6 6-6 6')],
  arrowUpRight: [p('M7 17 17 7'), p('M8.5 7H17v8.5')],
  // Appel reçu, dans le fil : la flèche qui revient vers soi.
  arrowDownLeft: [p('M17 7 7 17'), p('M15.5 17H7V8.5')],
  trending: [p('m3.5 16.8 5.8-5.8 3.6 3.6 7.6-7.6'), p('M15.4 7h5.1v5.1')],
  // Répondre à un commentaire : la flèche revient vers celui qui a écrit.
  reply: [p('M9.5 6.5 4 12l5.5 5.5'), p('M4 12h9.5a6.5 6.5 0 0 1 6.5 6.5')],
  // Effacer un chiffre du code de confidentialité.
  backspace: [
    p(
      'M9.2 5.5h9.3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9.2a2 2 0 0 1-1.5-.7L3 12l4.7-5.8a2 2 0 0 1 1.5-.7Z',
    ),
    p('m12 9.8 4.4 4.4'),
    p('M16.4 9.8 12 14.2'),
  ],
  refresh: [p('M20.2 12a8.2 8.2 0 1 1-2.6-6'), p('M20.5 3.6V9h-5.4')],
  exit: [
    p('M15 4.5H6.5a1.5 1.5 0 0 0-1.5 1.5v12a1.5 1.5 0 0 0 1.5 1.5H15'),
    p('M19.5 12H10'),
    p('m16 8.5 3.5 3.5-3.5 3.5'),
  ],

  // ------------------------------------------------------------ actions sociales
  heart: [
    p(
      'M20.8 8.6c0 4.7-8.8 10.6-8.8 10.6S3.2 13.3 3.2 8.6a4.7 4.7 0 0 1 8.8-2 4.7 4.7 0 0 1 8.8 2Z',
    ),
  ],
  heartFill: [
    f(
      'M20.8 8.6c0 4.7-8.8 10.6-8.8 10.6S3.2 13.3 3.2 8.6a4.7 4.7 0 0 1 8.8-2 4.7 4.7 0 0 1 8.8 2Z',
    ),
  ],
  thumb: [
    p(
      'M7.5 20.5V10l4-6.4a2 2 0 0 1 3.6 1.6L14 10h4.9a2 2 0 0 1 1.9 2.5l-1.7 6.5a2 2 0 0 1-1.9 1.5Z',
    ),
    p('M7.5 10.4h-3v10.1h3'),
  ],

  // Les trois réactions : « J'adore » (le cœur, plus haut), « Bof » et « Je
  // n'aime pas ». Le pouce baissé est `thumb` retourné d'un demi-tour, trait
  // pour trait ; le visage « Bof » a la bouche droite, ni sourire ni moue.
  // Leurs versions pleines se creusent : les yeux et la bouche laissent voir
  // le fond, l'icône reste d'une seule couleur.
  thumbDown: [
    p(
      'M16.5 3.5V14l-4 6.4a2 2 0 0 1-3.6-1.6L10 14H5.1a2 2 0 0 1-1.9-2.5l1.7-6.5a2 2 0 0 1 1.9-1.5Z',
    ),
    p('M16.5 13.6h3V3.5h-3'),
  ],
  thumbDownFill: [
    f(
      'M16.5 3.5V14l-4 6.4a2 2 0 0 1-3.6-1.6L10 14H5.1a2 2 0 0 1-1.9-2.5l1.7-6.5a2 2 0 0 1 1.9-1.5Z',
    ),
    f('M18.6 3.5h1a.9.9 0 0 1 .9.9v8.3a.9.9 0 0 1-.9.9h-1a.9.9 0 0 1-.9-.9V4.4a.9.9 0 0 1 .9-.9Z'),
  ],
  meh: [p(circle(12, 12, 8.6)), dot(9.1, 10.1, 1.15), dot(14.9, 10.1, 1.15), p('M8.9 14.9h6.2')],
  mehFill: [
    f(
      `${circle(12, 12, 8.9)}${circle(9.1, 10.1, 1.2)}${circle(14.9, 10.1, 1.2)}M9.4 14.1h5.2a.8.8 0 0 1 0 1.6H9.4a.8.8 0 0 1 0-1.6Z`,
      'evenodd',
    ),
  ],
  comment: [
    p(
      'M20.5 11.8a7.9 7.9 0 0 1-8.5 7.7 9.6 9.6 0 0 1-3.4-.6L4 20.5l1.3-4a7.4 7.4 0 0 1-1.8-4.7A7.9 7.9 0 0 1 12 4.1a7.9 7.9 0 0 1 8.5 7.7Z',
    ),
  ],
  share: [
    p('M4.5 12.5V19a1.5 1.5 0 0 0 1.5 1.5h12a1.5 1.5 0 0 0 1.5-1.5v-6.5'),
    p('M12 15V3.8'),
    p('m8 7.8 4-4 4 4'),
  ],
  bookmark: [p('M7 3.8h10a1 1 0 0 1 1 1V21l-6-4.2L6 21V4.8a1 1 0 0 1 1-1Z')],
  bookmarkFill: [f('M7 3.8h10a1 1 0 0 1 1 1V21l-6-4.2L6 21V4.8a1 1 0 0 1 1-1Z')],
  moreH: [dot(5.2, 12, 1.4), dot(12, 12, 1.4), dot(18.8, 12, 1.4)],
  moreV: [dot(12, 5.2, 1.4), dot(12, 12, 1.4), dot(12, 18.8, 1.4)],
  flag: [p('M6 21.2V3.5'), p('M6 4.4h11.4l-2.2 3.7 2.2 3.7H6')],
  flagFill: [f('M6 4.4h11.4l-2.2 3.7 2.2 3.7H6Z'), s('M6 21.2V3.5', { strokeWidth: 1.9 })],

  // --------------------------------------------------------------------- confiance
  shield: [
    p('m12 3.2 7.6 3v6.1c0 4.7-3.2 8-7.6 9.2-4.4-1.2-7.6-4.5-7.6-9.2V6.2Z'),
    p('m8.8 12.2 2.3 2.3 4.3-4.5'),
  ],
  // La coche est blanche : elle se pose sur l'encre de l'onglet actif, comme
  // celle de la pastille de certification sur l'or.
  shieldFill: [
    f('m12 3.2 7.6 3v6.1c0 4.7-3.2 8-7.6 9.2-4.4-1.2-7.6-4.5-7.6-9.2V6.2Z'),
    s('m8.8 12.2 2.3 2.3 4.3-4.5', { color: '#FFFFFF', strokeWidth: 1.9 }),
  ],
  /** Pastille de certification. La coche est blanche : elle se pose sur l'or. */
  verified: [
    f(
      'm12 2.1 2.4 2 3.1-.2.9 3 2.6 1.7-1.4 2.8 1.4 2.8-2.6 1.7-.9 3-3.1-.2-2.4 2-2.4-2-3.1.2-.9-3L3 14.2l1.4-2.8L3 8.6l2.6-1.7.9-3 3.1.2Z',
    ),
    s('m8.5 12.1 2.4 2.4 4.6-4.9', { color: '#FFFFFF', strokeWidth: 1.9 }),
  ],
  check: [p('m4.8 12.4 5 5L19.4 6.8')],
  checkCircle: [p(circle(12, 12, 8.6)), p('m8.2 12.3 2.7 2.7 5.1-5.4')],
  star: [p('m12 3.6 2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.3-4.1 5.9-.9Z')],
  starFill: [f('m12 3.6 2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.3-4.1 5.9-.9Z')],
  clock: [p(circle(12, 12, 8.5)), p('M12 7.4V12l3 1.9')],
  alert: [p('M12 4.4 20.7 19.6H3.3Z'), p('M12 10.2v4'), p('M12 17.2h.01')],
  info: [p(circle(12, 12, 8.6)), p('M12 11.2v5'), p('M12 7.9h.01')],
  close: [p('m6.2 6.2 11.6 11.6M17.8 6.2 6.2 17.8')],
  sparkle: [
    p('m11 3 1.9 5 5 1.9-5 1.9L11 16.8 9.1 11.8l-5-1.9 5-1.9Z'),
    p('m18 15.4.9 2.3 2.3.9-2.3.9-.9 2.3-.9-2.3-2.3-.9 2.3-.9Z'),
  ],
  lock: [
    p(
      'M7.2 10.6h9.6a2.4 2.4 0 0 1 2.4 2.4v5a2.4 2.4 0 0 1-2.4 2.4H7.2a2.4 2.4 0 0 1-2.4-2.4v-5a2.4 2.4 0 0 1 2.4-2.4Z',
    ),
    p('M8.2 10.6V7.8a3.8 3.8 0 0 1 7.6 0v2.8'),
  ],
  eye: [
    p('M2.6 12S6.3 5.9 12 5.9 21.4 12 21.4 12 17.7 18.1 12 18.1 2.6 12 2.6 12Z'),
    p(circle(12, 12, 3.1)),
  ],
  /** L'oeil barré : mot de passe masqué. Meme oeil que `eye`, une barre en plus —
   *  la paire doit se lire comme un interrupteur, pas comme deux icones. */
  eyeOff: [
    p('M2.6 12S6.3 5.9 12 5.9 21.4 12 21.4 12 17.7 18.1 12 18.1 2.6 12 2.6 12Z'),
    p(circle(12, 12, 3.1)),
    p('m4.4 4.4 15.2 15.2'),
  ],

  // --------------------------------------------------------------------- commerce
  store: [
    p('M4.4 9.2 5.7 4.4h12.6l1.3 4.8'),
    p('M4.7 9.2v10a1.4 1.4 0 0 0 1.4 1.4h11.8a1.4 1.4 0 0 0 1.4-1.4v-10'),
    p('M4.4 9.2a2.7 2.7 0 0 0 3.8 0 2.7 2.7 0 0 0 3.8 0 2.7 2.7 0 0 0 3.8 0 2.7 2.7 0 0 0 3.8 0'),
    p('M9.6 20.6v-5.4h4.8v5.4'),
  ],
  tag: [
    p('M11.6 3.5h7.9a1 1 0 0 1 1 1v7.9l-8.5 8.5a1.8 1.8 0 0 1-2.5 0l-6.4-6.4a1.8 1.8 0 0 1 0-2.5Z'),
    p(circle(16.4, 7.6, 1.4)),
  ],
  tagFill: [
    f(
      'M11.6 3.4h8a1 1 0 0 1 1 1v8l-8.6 8.6a1.9 1.9 0 0 1-2.7 0l-6.3-6.3a1.9 1.9 0 0 1 0-2.7Zm4.8 2.8a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z',
      'evenodd',
    ),
  ],
  card: [
    p(
      'M5.4 5.4h13.2a2.6 2.6 0 0 1 2.6 2.6v8a2.6 2.6 0 0 1-2.6 2.6H5.4a2.6 2.6 0 0 1-2.6-2.6v-8a2.6 2.6 0 0 1 2.6-2.6Z',
    ),
    p('M2.8 10h18.4'),
    p('M6.6 14.6h3.4'),
  ],
  cash: [
    p(
      'M4.8 6.4h14.4a2.2 2.2 0 0 1 2.2 2.2v6.8a2.2 2.2 0 0 1-2.2 2.2H4.8a2.2 2.2 0 0 1-2.2-2.2V8.6a2.2 2.2 0 0 1 2.2-2.2Z',
    ),
    p(circle(12, 12, 2.6)),
    p('M6.2 10v4M17.8 10v4'),
  ],
  wallet: [
    p('M3.6 8.4a2 2 0 0 1 2-2h11.6'),
    p(
      'M6.2 6.4h11.6a2.6 2.6 0 0 1 2.6 2.6v8.6a2.6 2.6 0 0 1-2.6 2.6H6.2a2.6 2.6 0 0 1-2.6-2.6V9a2.6 2.6 0 0 1 2.6-2.6Z',
    ),
    p('M3.6 11.4h16.8'),
    dot(16.8, 15.8, 1.1),
  ],
  truck: [
    p('M2.8 6.6h10.6v10.2H2.8Z'),
    p('M13.4 10.2h3.7l3.9 3.6v3h-7.6'),
    p(circle(7.2, 18.6, 1.9)),
    p(circle(17, 18.6, 1.9)),
  ],
  box: [
    p('m12 3.2 8.2 4.3v9L12 20.8 3.8 16.5v-9Z'),
    p('m3.8 7.5 8.2 4.4 8.2-4.4'),
    p('M12 11.9v8.9'),
  ],
  boxFill: [f('M12 2.9 3.4 7.4v9.2L12 21.1l8.6-4.5V7.4Zm0 8.5L4.8 7.6 12 4.4l7.2 3.2Z', 'evenodd')],
  receipt: [
    p('M6 3.4h12v17.2l-2.4-1.5-2.4 1.5-2.4-1.5-2.4 1.5L6 20.6Z'),
    p('M9.2 8.4h5.6M9.2 12.2h5.6M9.2 15.8h3.2'),
  ],
  receiptFill: [
    f('M6 3.4h12v17.2l-2.4-1.5-2.4 1.5-2.4-1.5-2.4 1.5L6 20.6Z'),
    s('M9.2 8.4h5.6M9.2 12.2h5.6M9.2 15.8h3.2', { color: '#FFFFFF', strokeWidth: 1.9 }),
  ],
  qr: [
    p('M4 4h5.2v5.2H4Z'),
    p('M14.8 4H20v5.2h-5.2Z'),
    p('M4 14.8h5.2V20H4Z'),
    p('M14.8 14.8h2v2h-2Z'),
    p('M18.4 14.8H20M14.8 18.4v1.6M18 18h2v2h-2Z'),
  ],
  chart: [p('M3.6 20.4h16.8'), p('M6.4 20.4v-6.2M12 20.4V5.6M17.6 20.4v-9.4')],
  chartFill: [
    f('M5.6 13.4h1.6a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H5.6a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1Z'),
    f('M11.4 5.4H13a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-1.6a1 1 0 0 1-1-1v-13a1 1 0 0 1 1-1Z'),
    f('M17.2 9.6h1.6a1 1 0 0 1 1 1v8.8a1 1 0 0 1-1 1h-1.6a1 1 0 0 1-1-1v-8.8a1 1 0 0 1 1-1Z'),
  ],
  percent: [p('m6 18 12-12'), p(circle(7.6, 7.6, 2.4)), p(circle(16.4, 16.4, 2.4))],

  // ------------------------------------------------------------ formulaire, fichiers
  sliders: [
    p('M4 7.4h4.2M13.4 7.4H20'),
    p(circle(10.8, 7.4, 2.3)),
    p('M4 16.6h6.4M15.6 16.6H20'),
    p(circle(13, 16.6, 2.3)),
  ],
  camera: [
    p(
      'M4.4 8.2h2.9L8.9 5.6h6.2l1.6 2.6h2.9a1.6 1.6 0 0 1 1.6 1.6v8.4a1.6 1.6 0 0 1-1.6 1.6H4.4a1.6 1.6 0 0 1-1.6-1.6V9.8a1.6 1.6 0 0 1 1.6-1.6Z',
    ),
    p(circle(12, 13.6, 3.4)),
  ],
  image: [
    p(
      'M6 4.8h12a2.6 2.6 0 0 1 2.6 2.6v9.2a2.6 2.6 0 0 1-2.6 2.6H6a2.6 2.6 0 0 1-2.6-2.6V7.4A2.6 2.6 0 0 1 6 4.8Z',
    ),
    p(circle(8.8, 10, 1.7)),
    p('m4.2 17.4 4.8-4.8 3.4 3.4 3-3 5.2 5.2'),
  ],
  // Plein, et non au trait : posé sur une vignette, un triangle creux se perd
  // dans l'image. C'est le seul de la famille qui ne suive pas la regle du
  // trait, et c'est parce qu'il ne se lit jamais sur fond uni.
  play: [f('M8.8 5.8 18.5 12l-9.7 6.2Z')],
  /** Le direct : une antenne qui émet. */
  broadcast: [
    dot(12, 12, 2.2),
    p('M8.2 15.8a5.4 5.4 0 0 1 0-7.6'),
    p('M15.8 8.2a5.4 5.4 0 0 1 0 7.6'),
    p('M5.4 18.6a9.4 9.4 0 0 1 0-13.2'),
    p('M18.6 5.4a9.4 9.4 0 0 1 0 13.2'),
  ],
  volume: [
    p('M4 9.6h3.4L12 5.8v12.4l-4.6-3.8H4Z'),
    p('M15.6 9a4.2 4.2 0 0 1 0 6'),
    p('M18.4 6.4a8 8 0 0 1 0 11.2'),
  ],
  volumeOff: [
    p('M4 9.6h3.4L12 5.8v12.4l-4.6-3.8H4Z'),
    p('M16 9.6l4.8 4.8'),
    p('M20.8 9.6 16 14.4'),
  ],
  cameraFlip: [
    p(
      'M4.4 8.4h2.8l1.4-2.2h6.8l1.4 2.2h2.8a1.6 1.6 0 0 1 1.6 1.6v7.6a1.6 1.6 0 0 1-1.6 1.6H4.4a1.6 1.6 0 0 1-1.6-1.6V10a1.6 1.6 0 0 1 1.6-1.6Z',
    ),
    p('M9.2 13.2a2.9 2.9 0 0 1 5.4-1.4'),
    p('M14.8 13.2a2.9 2.9 0 0 1-5.4 1.4'),
    p('M14.6 10.2v1.8h-1.8'),
    p('M9.4 16.2v-1.8h1.8'),
  ],
  video: [
    p('M4.4 6.4h9.6a2 2 0 0 1 2 2v7.2a2 2 0 0 1-2 2H4.4a2 2 0 0 1-2-2V8.4a2 2 0 0 1 2-2Z'),
    p('M16 10.4 21.6 7.6v8.8L16 13.6Z'),
  ],
  videoOff: [
    p('M4.4 6.4h9.6a2 2 0 0 1 2 2v7.2a2 2 0 0 1-2 2H4.4a2 2 0 0 1-2-2V8.4a2 2 0 0 1 2-2Z'),
    p('M16 10.4 21.6 7.6v8.8L16 13.6Z'),
    p('m3.4 3.4 17.2 17.2'),
  ],
  file: [
    p('M6.4 3.4h6.8l4.4 4.4v12.8H6.4Z'),
    p('M13.2 3.4v4.4h4.4'),
    p('M9.4 13h5.2M9.4 16.4h5.2'),
  ],
  id: [
    p(
      'M5.2 5h13.6a2.6 2.6 0 0 1 2.6 2.6v8.8a2.6 2.6 0 0 1-2.6 2.6H5.2a2.6 2.6 0 0 1-2.6-2.6V7.6A2.6 2.6 0 0 1 5.2 5Z',
    ),
    p(circle(8.6, 10.8, 2.3)),
    p('M5.2 16.4a3.6 3.6 0 0 1 6.8 0'),
    p('M14.8 10h4.4M14.8 13.6h3.2'),
  ],
  upload: [
    p('M12 15.8V4.2'),
    p('m7.6 8.6 4.4-4.4 4.4 4.4'),
    p('M4.2 16v3.2a1.4 1.4 0 0 0 1.4 1.4h12.8a1.4 1.4 0 0 0 1.4-1.4V16'),
  ],
  edit: [p('M4.2 19.8h4L19.4 8.6a2.1 2.1 0 0 0-3-3L5.2 16.8Z'), p('m14.6 6.6 3 3')],
  trash: [
    p('M4.6 6.8h14.8'),
    p('M9.4 6.8V4.4h5.2v2.4'),
    p('m6.6 6.8 1 12.5a1.2 1.2 0 0 0 1.2 1.1h6.4a1.2 1.2 0 0 0 1.2-1.1l1-12.5'),
    p('M10.2 10.6v6M13.8 10.6v6'),
  ],
  send: [p('M20.8 3.4 3.6 10.6l7.2 2.6 2.6 7.4Z'), p('M10.8 13.2 20.8 3.4')],
  clip: [
    p(
      'm19.6 11.4-8.2 8.2a4.8 4.8 0 0 1-6.8-6.8l8.6-8.6a3.2 3.2 0 0 1 4.6 4.6l-8.6 8.6a1.7 1.7 0 0 1-2.4-2.4l7.9-7.9',
    ),
  ],
  calendar: [
    p(
      'M6 5.2h12a2.6 2.6 0 0 1 2.6 2.6v10.2a2.6 2.6 0 0 1-2.6 2.6H6a2.6 2.6 0 0 1-2.6-2.6V7.8A2.6 2.6 0 0 1 6 5.2Z',
    ),
    p('M3.4 10.2h17.2'),
    p('M8.2 3.2v4M15.8 3.2v4'),
  ],
  calendarFill: [
    f(
      'M6 5.2h12a2.6 2.6 0 0 1 2.6 2.6v10.2a2.6 2.6 0 0 1-2.6 2.6H6a2.6 2.6 0 0 1-2.6-2.6V7.8A2.6 2.6 0 0 1 6 5.2Z',
    ),
    // Le filet d'en-tete se creuse en blanc ; les attaches, dans l'encre de
    // l'icone, dépassent du corps plein et s'y fondent en dessous.
    s('M3.4 10.2h17.2', { color: '#FFFFFF', strokeWidth: 1.9 }),
    s('M8.2 3.2v3M15.8 3.2v3', { strokeWidth: 1.9 }),
  ],
  pin: [p('M12 20.8s7-5.7 7-11a7 7 0 1 0-14 0c0 5.3 7 11 7 11Z'), p(circle(12, 9.8, 2.6))],
  phone: [
    p(
      'M6.4 3.6h3.9l2 4.9-2.5 1.5a12 12 0 0 0 4.2 4.2l1.5-2.5 4.9 2v3.9a1.6 1.6 0 0 1-1.8 1.6C10.3 18.4 5.6 13.7 4.8 5.4a1.6 1.6 0 0 1 1.6-1.8Z',
    ),
  ],
  /** Raccrocher : le combiné posé à plat. */
  phoneEnd: [
    p(
      'M3.4 13.6c4.9-4.4 12.3-4.4 17.2 0a1.5 1.5 0 0 1 .1 2.1l-1.6 1.8a1.2 1.2 0 0 1-1.6.2l-2.7-1.9a1.2 1.2 0 0 1-.5-1.2l.4-1.9a10.4 10.4 0 0 0-5.4 0l.4 1.9a1.2 1.2 0 0 1-.5 1.2l-2.7 1.9a1.2 1.2 0 0 1-1.6-.2l-1.6-1.8a1.5 1.5 0 0 1 .1-2.1Z',
    ),
  ],
  mail: [
    p(
      'M5.6 5.4h12.8a2.6 2.6 0 0 1 2.6 2.6v8a2.6 2.6 0 0 1-2.6 2.6H5.6A2.6 2.6 0 0 1 3 16V8a2.6 2.6 0 0 1 2.6-2.6Z',
    ),
    p('m3.6 7.8 8.4 5.8 8.4-5.8'),
  ],
  globe: [
    p(circle(12, 12, 8.6)),
    p('M3.4 12h17.2'),
    p('M12 3.4a13.2 13.2 0 0 1 0 17.2 13.2 13.2 0 0 1 0-17.2Z'),
  ],
  settings: [
    p(circle(12, 12, 3.1)),
    p(
      'M18.9 14.4a1.6 1.6 0 0 0 .3 1.8l.1.1a1.9 1.9 0 1 1-2.7 2.7l-.1-.1a1.6 1.6 0 0 0-2.7 1.1v.3a1.9 1.9 0 0 1-3.8 0v-.2a1.6 1.6 0 0 0-2.8-1.1l-.1.1a1.9 1.9 0 1 1-2.7-2.7l.1-.1a1.6 1.6 0 0 0-1.1-2.7h-.3a1.9 1.9 0 0 1 0-3.8h.2a1.6 1.6 0 0 0 1.1-2.8l-.1-.1a1.9 1.9 0 1 1 2.7-2.7l.1.1a1.6 1.6 0 0 0 2.7-1.1v-.3a1.9 1.9 0 0 1 3.8 0v.2a1.6 1.6 0 0 0 2.8 1.1l.1-.1a1.9 1.9 0 1 1 2.7 2.7l-.1.1a1.6 1.6 0 0 0 1.1 2.7h.3a1.9 1.9 0 0 1 0 3.8h-.2a1.6 1.6 0 0 0-1.4.9Z',
    ),
  ],

  // ------------------------------------------------------------ propres au web
  // Même grille, même trait : ce que le navigateur demande en plus du téléphone
  // (thème, lecture, clavier, liens).
  sun: [
    p(circle(12, 12, 3.8)),
    p(
      'M12 2.8v1.9M12 19.3v1.9M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2.8 12h1.9M19.3 12h1.9M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4',
    ),
  ],
  moon: [p('M20 14.6A8.4 8.4 0 0 1 9.4 4a8.4 8.4 0 1 0 10.6 10.6Z')],
  monitor: [
    p('M5.4 4.4h13.2a2 2 0 0 1 2 2v8.2a2 2 0 0 1-2 2H5.4a2 2 0 0 1-2-2V6.4a2 2 0 0 1 2-2Z'),
    p('M9 20.4h6M12 16.6v3.8'),
  ],
  pause: [
    f(
      'M7.2 4.6h2.8a.8.8 0 0 1 .8.8v13.2a.8.8 0 0 1-.8.8H7.2a.8.8 0 0 1-.8-.8V5.4a.8.8 0 0 1 .8-.8Zm6.8 0h2.8a.8.8 0 0 1 .8.8v13.2a.8.8 0 0 1-.8.8H14a.8.8 0 0 1-.8-.8V5.4a.8.8 0 0 1 .8-.8Z',
    ),
  ],
  // Le menu : deux barres, la seconde plus courte. Trois barres égales font un
  // pavé ; deux suffisent à dire « menu » et laissent respirer l'en-tête.
  menu: [p('M4 8.5h16'), p('M4 15.5h11')],
  expand: [p('M14.6 4H20v5.4M9.4 20H4v-5.4M20 4l-6.2 6.2M4 20l6.2-6.2')],
  collapse: [p('M20 9.4h-5.4V4M4 14.6h5.4V20M14.6 9.4 20.8 3.2M9.4 14.6l-6.2 6.2')],
  mic: [
    p('M12 3.4a3 3 0 0 1 3 3v5.4a3 3 0 0 1-6 0V6.4a3 3 0 0 1 3-3Z'),
    p('M18.2 11.4a6.2 6.2 0 0 1-12.4 0M12 17.6v3'),
  ],
  micOff: [
    p('M15 10.2V6.4a3 3 0 0 0-5.6-1.5M9 9.2v2.6a3 3 0 0 0 4.6 2.5'),
    p('M18.2 11.4a6.2 6.2 0 0 1-.8 3M5.8 11.4a6.2 6.2 0 0 0 9.9 5M12 17.6v3M3.6 3.6l16.8 16.8'),
  ],
  link: [
    p('M10.2 13.8a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1.2 1.2'),
    p('M13.8 10.2a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1.2-1.2'),
  ],
  copy: [
    p(
      'M9.4 8.6h8.4a1.8 1.8 0 0 1 1.8 1.8v8.4a1.8 1.8 0 0 1-1.8 1.8H9.4a1.8 1.8 0 0 1-1.8-1.8v-8.4a1.8 1.8 0 0 1 1.8-1.8Z',
    ),
    p(
      'M15.4 8.6V6.2a1.8 1.8 0 0 0-1.8-1.8H6.2a1.8 1.8 0 0 0-1.8 1.8v7.4a1.8 1.8 0 0 0 1.8 1.8h1.4',
    ),
  ],
  download: [p('M12 4v11'), p('m7.2 10.6 4.8 4.8 4.8-4.8'), p('M4.5 19.5h15')],
  external: [
    p('M13.6 4.4H19.6v6M19.6 4.4l-8.2 8.2'),
    p(
      'M17.6 13.6v4.2a1.8 1.8 0 0 1-1.8 1.8H6.2a1.8 1.8 0 0 1-1.8-1.8V8.2a1.8 1.8 0 0 1 1.8-1.8h4.2',
    ),
  ],
  keyboard: [
    p(
      'M4.6 6.4h14.8a1.8 1.8 0 0 1 1.8 1.8v7.6a1.8 1.8 0 0 1-1.8 1.8H4.6a1.8 1.8 0 0 1-1.8-1.8V8.2a1.8 1.8 0 0 1 1.8-1.8Z',
    ),
    p('M7 10h.01M10.4 10h.01M13.6 10h.01M17 10h.01M8.2 13.8h7.6'),
  ],
} as const satisfies Record<string, ReadonlyArray<IconPath>>;

export type IconName = keyof typeof iconPaths;

/** Toutes les icones, pour la galerie du design system. */
export const iconNames = Object.keys(iconPaths) as ReadonlyArray<IconName>;
